import { ChangeDetectorRef, Directive, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LibraryRouter } from '@app/models/library.router';
import { videoType, vimeoCoverImageUrl, youtubeCoverImageUrl } from '@core/helpers/video';
import {
  AccountService,
  ContentService,
  FilesService,
  PlatformService,
  RequestService,
  ScreenService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  Content,
  ContentTypeEnum,
  EContentPaneFormat,
  ImageSizeEnum,
  LessonContentType,
  Router as ContentRouter,
  SubjectCategoryEnum,
  User,
  Video as VideoContent,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui';
import { takeUntil } from 'rxjs/operators';

@Directive()
// tslint:disable-next-line:directive-class-suffix
export abstract class BaseContentCardMiniComponent extends ChangableComponent implements OnInit {
  @Input() set item(item: Content) {
    this._item = item;
    this.fullTitle = this.contentService.contentFullTitle(item);
    this.prepareContentCard();
    this.onBrowserOnly(() => {
      this.loadIsSubscribed();
    });
    this.onChanged(true);
  }

  get item(): Content {
    return this._item;
  }

  public get isLesson(): boolean {
    return LessonContentType.includes(this.item.type);
  }

  public get isVideo(): boolean {
    return this.item.type === ContentTypeEnum.video;
  }

  public get isAudio(): boolean {
    return this.item.type === ContentTypeEnum.audio;
  }

  public get isArticleOrNews(): boolean {
    return this.item.type === ContentTypeEnum.article || this.item.type === ContentTypeEnum.news;
  }

  @Input() active: boolean;
  @Input() linkEnable = false;
  @Input() format: EContentPaneFormat;
  @Input() infinity: boolean;
  public accountRoute = AccountService.accountRoute;
  public contentRoute = LibraryRouter.contentRouteUniversal;
  public hasCoverImage = true;
  public author: User;
  public authorName: string;
  public authorFullName: string;
  public subject: string;
  public level: string;
  public contentUrl: string;
  public contentTypeTitle: string;
  public contentTypeUrl: string;
  public contentDate: Date;
  public userpicUrl: string;
  public coverImageUrl: string;
  public authorAvatar: OnUiCover;
  public cover: OnUiCover;
  public metaImageUrl: string;
  public fullTitle: string;
  public cutLength = 12;
  public subjectCategoryEnum = SubjectCategoryEnum;
  public description: string;
  public subscribed = false;

  private _item: Content;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private accountService: AccountService,
    private contentService: ContentService,
    private screenService: ScreenService,
    private translate: TranslateService,
    protected request: RequestService,
    protected router: Router,
    public deviceService: DeviceDetectorService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  ngOnInit() {
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (width) => {
        this.cutLength = width > 760 ? 20 : 15;
        this.markForCheck();
      },
      error: (error) => {
        this.cutLength = window.innerWidth > 760 ? 20 : 15;
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  public get isImage(): boolean {
    return this.item.type === ContentTypeEnum.image;
  }

  public get isMediaTypeNecessary() {
    return this.isVideo || this.isAudio || this.isImage;
  }

  private loadAuthorDetails(id: string) {
    if (!id) {
      return;
    }
    this.accountService
      .getUserById(id, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user) => {
          this.author = user;
          this.authorName = AccountService.getUserName(this.author);
          this.authorFullName = AccountService.getUserFullName(this.author);
          this.userpicUrl = FilesService.getFileUrlBySize(this.author?.userpic_id, ImageSizeEnum.sm);
          this.authorAvatar = {
            type: 'avatar',
            img: this.userpicUrl,
            title: this.authorFullName,
            link: this.accountRoute(this.author).join('/'),
            form: 'circle',
          };
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected prepareContentCard() {
    if (!this._item.author) {
      this.loadAuthorDetails(this._item.author_id);
    } else {
      this.author = this._item.author;
      this.authorName = AccountService.getUserName(this.author);
      this.authorFullName = AccountService.getUserFullName(this.author);
      this.userpicUrl =
        this.prepareStorageUrl(this.author?.__opts?.images[0].sm) ||
        FilesService.getFileUrlBySize(this.author?.userpic_id, ImageSizeEnum.sm);
      this.authorAvatar = {
        type: 'avatar',
        img: this.userpicUrl,
        title: this.authorFullName,
        link: this.accountRoute(this.author).join('/'),
        form: 'circle',
      };
    }
    this.subject = (this._item.subject ? this._item.subject.title : null) || '---';
    if (!!this._item.level_other_name) {
      this.level = this._item.level_other_name;
    } else {
      this.level =
        'level' in this._item && this._item.level
          ? this._item.level.title
          : this.translate.instant('subject-levels.any-level');
    }
    this.contentTypeTitle = ContentService.contentTypeTitle[this._item.type];
    this.contentTypeUrl = ContentRouter.getNameByContentType(this._item.type);
    this.contentDate = this._item.published_at ? new Date(this._item.published_at) : new Date(this._item.created_at);
    this.contentUrl = this._item ? this.contentRoute(this._item) : null;
    this.description = this._item?.description;
    this.prepareCoverImage();
    this.cover = {
      type: 'cover',
      img: this.coverImageUrl,
      title: this.fullTitle,
      link: this.contentUrl,
      form: 'base',
    };
    this.prepareMetaImage();
    this.detectChanges();
  }

  protected prepareCoverImage() {
    if (!this._item.cover && this.isVideo) {
      const _video = this._item as VideoContent;
      const _type = videoType(_video.media_url);
      switch (_type) {
        case 'youtube':
          this.coverImageUrl = youtubeCoverImageUrl(_video.media_url);
          break;

        case 'vimeo':
          this.coverImageUrl = vimeoCoverImageUrl(_video.media_url);
          break;
      }
      return;
    }

    this.coverImageUrl =
      this.prepareStorageUrl(this._item?.__opts?.images[0].mdX2) ||
      FilesService.getFileUrlBySize(this._item.cover_id, ImageSizeEnum.md);
  }

  protected prepareMetaImage() {
    if (this.isVideo) {
      this.metaImageUrl = this.coverImageUrl;
    } else {
      this.metaImageUrl =
        this.prepareStorageUrl(this._item?.__opts?.images[0].md) || FilesService.getFileUrlBySize(this.item.cover_id);
    }
  }

  protected loadIsSubscribed() {
    if (!this._item) {
      return;
    }
    this.accountService
      .getIsSubscribed(this._item.author_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (result) => {
          this.subscribed = result;
          this.markForCheck();
        },
        error: (error: any) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.markForCheck();
        },
      });
  }

  public onToggleSubscribe(event) {
    event.preventDefault();
    event.cancelBubble = true;
    this.markForCheck();
    if (this.subscribed) {
      this.accountService
        .unsubscribe(this._item.author_id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (result) => {
            this.subscribed = false;
            this.markForCheck();
          },
          error: (error: any) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
            this.markForCheck();
          },
        });
    } else {
      this.accountService
        .subscribe(this._item.author_id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (result) => {
            this.subscribed = true;
            this.markForCheck();
          },
          error: (error: any) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
            this.subscribed = false;
            this.markForCheck();
          },
        });
    }
  }

  protected abstract onChanged(viaQuery?: boolean): void;
}
